import { t, Trans } from '@lingui/macro'
import { Currency, Percent } from '@uniswap/sdk-core'
import Badge from 'components/Badge'
import Column from 'components/Column'
import CurrencyLogo from 'components/Logo/CurrencyLogo'
import Row, { AutoRow } from 'components/Row'
import useCopyClipboard from 'hooks/useCopyClipboard'
import { useTokenInfoFromActiveList } from 'hooks/useTokenInfoFromActiveList'
import { Fragment } from 'react'
import { Copy, ExternalLink as ExternalLinkIconFeather } from 'react-feather'
import { Box } from 'rebass'
import styled from 'styled-components/macro'
import { MEDIA_WIDTHS, ThemedText } from 'theme'
import { Z_INDEX } from 'theme/zIndex'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'
import { RoutingDiagramEntryX, RoutingDiagramPathX } from 'utils/getRoutingDiagramEntries'

import { ReactComponent as DotLine } from '../../assets/svg/dot_line.svg'

const Wrapper = styled(Box)`
  align-items: center;
  width: 100%;
  overflow-x: overlay;
`

const RouteContainerRow = styled(Row)`
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  @media screen and (max-width: ${MEDIA_WIDTHS.deprecated_upToExtraSmall}px) {
    grid-template-columns: 20px 1fr 20px;
    width: max-content;
  }
`

const HeadCol = styled(Column)`
  border-radius: 6px;
  margin-left: 5px;
  text-align: center;
  text-align: -webkit-center;
  z-index: 1;
  border: 1px solid ${({ theme }) => theme.backgroundInteractive};
  background-color: ${({ theme }) => theme.backgroundSurface};
`
const RouteRowX = styled(Row)`
  align-items: space-evenly;
  display: flex;
  justify-content: space-evenly;
  //padding: 0rem 0.5rem;
  padding: 0.5rem 0.5rem;
  position: relative;
  @media screen and (max-width: ${MEDIA_WIDTHS.deprecated_upToExtraSmall}px) {
    zoom: 0.85;
  }
`

const DottedLine = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  width: calc(100%);
  z-index: 1;
  opacity: 0.1;
`

const DotColor = styled(DotLine)`
  path {
    stroke: ${({ theme }) => theme.deprecated_bg4};
  }
`

const OpaqueBadge = styled(Badge)`
  background-color: transparent;
  border-radius: 6px;
  display: grid;
  font-size: 12px;
  grid-gap: 0px;
  grid-auto-flow: column;
  justify-content: start;
  padding: 0px;
  z-index: ${Z_INDEX.sticky};
`
const OpaqueBadgeX = styled(Badge)`
  background-color: ${({ theme }) => theme.backgroundInteractive};
  border-radius: 4px;
  display: grid;
  font-size: 12px;
  grid-gap: 4px;
  grid-auto-flow: column;
  justify-content: start;
  padding: 4px 6px 4px 4px;
  z-index: ${Z_INDEX.sticky};
`

const ProtocolBadge = styled(Badge)`
  background-color: transparent;
  border-radius: 0px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
  width: 80px;
  padding: 4px 4px;
  z-index: ${Z_INDEX.sticky + 1};
`

const ProtocolBadgeX = styled(Badge)`
  background-color: ${({ theme }) => theme.backgroundInteractive};
  border-radius: 6px 6px 0px 0px;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 11px;
  padding: 4px 4px;
  gap: 3px;
  font-weight: 600;
  width: 120px;
  z-index: ${Z_INDEX.sticky + 1};
`

const BadgeText = styled(ThemedText.DeprecatedSmall)`
  word-break: normal;
`

const EmptyState = styled(ThemedText.BodySmall)`
  width: 100%;
  text-align: center;
  padding: 1rem 0;
  color: ${({ theme }) => theme.textTertiary};
`

const TreeWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const TreeRouteCard = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 10px;
  padding: 12px;
  background: ${({ theme }) => theme.backgroundSurface};
  flex-wrap: wrap;
`

const TreePercentBadge = styled(OpaqueBadgeX)`
  min-width: 72px;
  justify-content: center;
`

const TreePathColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TreeTokenNode = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  padding: 4px 6px;
  background: ${({ theme }) => theme.backgroundInteractive};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  width: fit-content;
`

const TreeConnector = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-left: 18px;
`

const TreeConnectorLine = styled.div`
  width: 2px;
  border-radius: 999px;
  background: ${({ theme }) => theme.deprecated_bg4};
  margin-top: 4px;
  min-height: 38px;
`

const TreeConnectorContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const TreeConnectorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const TreeProtocolBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const TreeProtocolBadge = styled(Badge)`
  border-radius: 6px;
  padding: 4px 8px 4px 10px;
  font-size: 11px;
  background: ${({ theme }) => theme.backgroundInteractive};
  color: ${({ theme }) => theme.textPrimary};
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const TreeProtocolLink = styled.a`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.textSecondary};
  opacity: 0.75;
  transition: color 150ms ease, opacity 150ms ease;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    opacity: 1;
  }
`

const TreeProtocolLinkIcon = styled(ExternalLinkIconFeather)`
  width: 12px;
  height: 12px;
`

const TreeProtocolActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
`

const TreeProtocolCopyButton = styled.button`
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  padding: 0;
  color: ${({ theme }) => theme.textSecondary};
  opacity: 0.75;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    opacity: 1;
  }
`

export const ROUTING_DIAGRAM_VIEWS = {
  CLASSIC: 'classic',
  DETAILED: 'detailed',
} as const

export type RoutingDiagramViewMode = typeof ROUTING_DIAGRAM_VIEWS[keyof typeof ROUTING_DIAGRAM_VIEWS]

const formatPercentValue = (percent?: Percent, digits = 2) => {
  if (!percent) return '--'
  try {
    const fixed = percent.toFixed(digits)
    if (!fixed.includes('.')) {
      return fixed
    }
    const numeric = Number(fixed)
    if (Number.isInteger(numeric)) {
      return numeric.toString()
    }
    const trimmed = fixed.replace(/0+$/, '').replace(/\.$/, '')
    return trimmed.length ? trimmed : fixed
  } catch {
    return percent.toSignificant(digits)
  }
}

const buildTokenSequence = (subroutes: RoutingDiagramPathX[]) => {
  if (!subroutes.length) {
    return []
  }
  const sequence: Currency[] = []
  sequence.push(subroutes[0].tokenA)
  subroutes.forEach((subroute) => {
    sequence.push(subroute.tokenB)
  })
  return sequence
}

export default function RoutingDiagram({
  currencyIn,
  currencyOut,
  routes,
  chainId,
  viewMode = ROUTING_DIAGRAM_VIEWS.CLASSIC,
}: {
  currencyIn: Currency
  currencyOut: Currency
  routes: RoutingDiagramEntryX[]
  chainId?: number
  viewMode?: RoutingDiagramViewMode
}) {
  const tokenIn = useTokenInfoFromActiveList(currencyIn)
  const tokenOut = useTokenInfoFromActiveList(currencyOut)

  const effectiveIn = tokenIn ?? currencyIn
  const effectiveOut = tokenOut ?? currencyOut
  const fallbackTokens: [Currency, Currency] = [effectiveIn, effectiveOut]

  if (!routes || routes.length === 0) {
    return (
      <Wrapper>
        <EmptyState>
          <Trans>No routes available.</Trans>
        </EmptyState>
      </Wrapper>
    )
  }

  if (viewMode === ROUTING_DIAGRAM_VIEWS.DETAILED) {
    return (
      <Wrapper>
        <DetailedRoutes routes={routes} fallbackTokens={fallbackTokens} chainId={chainId} />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <ClassicRoutes routes={routes} tokenIn={effectiveIn} tokenOut={effectiveOut} />
    </Wrapper>
  )
}

function ClassicRoutes({
  routes,
  tokenIn,
  tokenOut,
}: {
  routes: RoutingDiagramEntryX[]
  tokenIn: Currency
  tokenOut: Currency
}) {
  return (
    <>
      {routes.map((entry, index) => (
        <RouteContainerRow key={`route-${index}`}>
          <CurrencyLogo currency={tokenIn} size="22px" />
          <Route entry={entry} />
          <CurrencyLogo currency={tokenOut} size="22px" />
        </RouteContainerRow>
      ))}
    </>
  )
}

function DetailedRoutes({
  routes,
  fallbackTokens,
  chainId,
}: {
  routes: RoutingDiagramEntryX[]
  fallbackTokens: [Currency, Currency]
  chainId?: number
}) {
  return (
    <TreeWrapper>
      {routes.map((entry, index) => (
        <DetailedRoute key={`tree-route-${index}`} entry={entry} fallbackTokens={fallbackTokens} chainId={chainId} />
      ))}
    </TreeWrapper>
  )
}

function DetailedRoute({
  entry,
  fallbackTokens,
  chainId,
}: {
  entry: RoutingDiagramEntryX
  fallbackTokens: [Currency, Currency]
  chainId?: number
}) {
  const sequence = buildTokenSequence(entry.routes)
  const tokens = sequence.length ? sequence : fallbackTokens
  const hasConnectors = entry.routes.length > 0

  return (
    <TreeRouteCard>
      <TreePercentBadge>
        <BadgeText fontSize={12}>{formatPercentValue(entry.percent)}%</BadgeText>
      </TreePercentBadge>
      <TreePathColumn>
        {tokens.map((token, index) => (
          <Fragment key={`${token?.symbol ?? 'token'}-${index}`}>
            <TreeTokenDisplay token={token} />
            {hasConnectors && index < entry.routes.length ? (
              <TreeConnector>
                <TreeConnectorLine />
                <TreeConnectorContent>
                  <TreeConnectorHeader>
                    <ThemedText.Caption fontWeight={600}>
                      {entry.routes[index]?.tokenA?.symbol} ➜ {entry.routes[index]?.tokenB?.symbol}
                    </ThemedText.Caption>
                    <TreeProtocolBadge>{formatPercentValue(entry.routes[index]?.percent)}%</TreeProtocolBadge>
                  </TreeConnectorHeader>
                  <TreeProtocolBadges>
                    {entry.routes[index]?.paths?.map((path, pathIndex) => {
                      const explorerUrl =
                        chainId && path.address
                          ? getExplorerLink(chainId, path.address, ExplorerDataType.ADDRESS)
                          : undefined
                      return (
                        <TreeProtocolBadge key={`tree-path-${pathIndex}`}>
                          <span>
                            {path.protocol} {formatPercentValue(path.percent, 2)}%
                          </span>
                          <ProtocolPathActions address={path.address} explorerUrl={explorerUrl} />
                        </TreeProtocolBadge>
                      )
                    })}
                  </TreeProtocolBadges>
                </TreeConnectorContent>
              </TreeConnector>
            ) : null}
          </Fragment>
        ))}
      </TreePathColumn>
    </TreeRouteCard>
  )
}

function ProtocolPathActions({ address, explorerUrl }: { address?: string; explorerUrl?: string }) {
  const [, copyAddress] = useCopyClipboard()

  if (!address && !explorerUrl) {
    return null
  }

  return (
    <TreeProtocolActions>
      {address ? (
        <TreeProtocolCopyButton type="button" aria-label={t`Copy pool address`} onClick={() => copyAddress(address)}>
          <Copy size={12} />
        </TreeProtocolCopyButton>
      ) : null}
      {explorerUrl ? (
        <TreeProtocolLink href={explorerUrl} target="_blank" rel="noreferrer" aria-label={t`Open in explorer`}>
          <TreeProtocolLinkIcon size={12} />
        </TreeProtocolLink>
      ) : null}
    </TreeProtocolActions>
  )
}

function TreeTokenDisplay({ token }: { token: Currency }) {
  const tokenInfo = useTokenInfoFromActiveList(token)
  const displayToken = tokenInfo ?? token

  if (!displayToken) {
    return (
      <TreeTokenNode>
        <ThemedText.Caption fontWeight={600}>--</ThemedText.Caption>
      </TreeTokenNode>
    )
  }

  return (
    <TreeTokenNode>
      <CurrencyLogo currency={displayToken} size="22px" />
      <ThemedText.Caption fontWeight={600}>{displayToken?.symbol ?? '--'}</ThemedText.Caption>
    </TreeTokenNode>
  )
}

function Route({ entry: { percent, routes } }: { entry: RoutingDiagramEntryX }) {
  return (
    <RouteRowX>
      <DottedLine>
        <DotColor />
      </DottedLine>
      <OpaqueBadgeX>
        <BadgeText fontSize={12} style={{ minWidth: 'auto' }}>
          {percent.toFixed(2)}%
        </BadgeText>
      </OpaqueBadgeX>
      <Row justify="space-around" align="center">
        {routes.map((subroutes: any, index) => (
          <Subroute entry={subroutes} key={index} />
        ))}
        {/*<AutoRow gap="1px" width="100%" style={{ justifyContent: 'space-evenly', zIndex: 2 }}>
        {path.map(([currency0, currency1, feeAmount], index) => (
          <Pool key={index} currency0={currency0} currency1={currency1} feeAmount={feeAmount} />
        ))}
      </AutoRow>*/}
      </Row>
    </RouteRowX>
  )
}

function Subroute({ entry: { paths, tokenA, tokenB } }: { entry: RoutingDiagramPathX }) {
  const tokenInfo0 = useTokenInfoFromActiveList(tokenA)
  const tokenInfo1 = useTokenInfoFromActiveList(tokenB)

  return (
    <HeadCol>
      <AutoRow
        style={{
          justifyContent: 'space-around!important',
        }}
      >
        <OpaqueBadge>
          <ProtocolBadgeX>
            {/*<CurrencyLogo currency={tokenIn} size={'24px'} /> {tokenIn?.symbol}*/}
            {/*tokenInfo0 && tokenInfo1 ? (
              <DoubleCurrencyLogo currency0={tokenInfo1} currency1={tokenInfo0} size={17} />
            ) : null*/}
            {tokenInfo0?.symbol} ➜ {tokenInfo1?.symbol}
          </ProtocolBadgeX>
        </OpaqueBadge>
      </AutoRow>
      <AutoRow
        gap="0px"
        style={{
          justifyContent: 'space-evenly',
          padding: '4px 0px',
          borderRadius: '6px',
          display: 'inline-block',
          alignItems: 'baseline',
          zIndex: 2,
        }}
      >
        {paths.map((path: any, index) => (
          <Path key={index} percent={path.percent} protocol={path.protocol} />
        ))}
      </AutoRow>
    </HeadCol>
  )
}

function Path({ percent, protocol }: { percent: Percent; protocol: string }) {
  // TODO - link pool icon to info.uniswap.org via query params
  // tokenInfo0?.symbol + '/' + tokenInfo1?.symbol + ' ' +
  return (
    <AutoRow>
      <OpaqueBadge>
        <ProtocolBadge>
          <BadgeText fontSize={11} fontWeight={600}>
            {protocol}{' '}
          </BadgeText>
        </ProtocolBadge>
        <BadgeText fontSize={11} style={{ minWidth: 'auto' }}>
          {percent.toSignificant(3)}%
        </BadgeText>
      </OpaqueBadge>
      {/*<AutoRow gap="1px" width="100%" style={{ justifyContent: 'space-evenly', zIndex: 2 }}>
        {path.map(([currency0, currency1, feeAmount], index) => (
          <Pool key={index} currency0={currency0} currency1={currency1} feeAmount={feeAmount} />
        ))}
      </AutoRow>*/}
    </AutoRow>
  )
}
